import {
    MATCH_BAND,
    REVIEW_MATCH_THRESHOLD,
    STRONG_MATCH_THRESHOLD,
} from 'constants/documentVerification';

/**
 * Map a 0..1 face-match score to a plain-language band for staff.
 * @param {number|null|undefined} score
 * @returns {{key: string, label: string, color: string}}
 */
export function getMatchBand(score) {
    if (score === null || score === undefined || Number.isNaN(Number(score))) {
        return MATCH_BAND.UNKNOWN;
    }
    const value = Number(score);
    if (value >= STRONG_MATCH_THRESHOLD) {
        return MATCH_BAND.STRONG;
    }
    if (value >= REVIEW_MATCH_THRESHOLD) {
        return MATCH_BAND.REVIEW;
    }
    return MATCH_BAND.LOW;
}

/** Render a 0..1 score as a percentage string, or a dash when absent. */
export function formatScorePercent(score) {
    if (score === null || score === undefined || Number.isNaN(Number(score))) {
        return '—';
    }
    return `${Math.round(Number(score) * 100)}%`;
}

function nameTokens(value) {
    if (!value) {
        return [];
    }
    return String(value)
        .toUpperCase()
        .replace(/[^A-Z\s]/g, ' ')
        .split(/\s+/)
        .filter(Boolean);
}

/**
 * Lenient name comparison: true when the two names share the same set of tokens
 * regardless of order (OCR/MRZ order differs from entered order, may include middle names).
 * Returns null when either side is missing (can't compare → not a mismatch, needs manual check).
 * @returns {boolean|null}
 */
export function namesMatch(ocrName, enteredName) {
    const ocr = nameTokens(ocrName);
    const entered = nameTokens(enteredName);
    if (!ocr.length || !entered.length) {
        return null;
    }
    const ocrSet = new Set(ocr);
    const enteredSet = new Set(entered);
    const enteredInOcr = entered.every((token) => ocrSet.has(token));
    const ocrInEntered = ocr.every((token) => enteredSet.has(token));
    return enteredInOcr || ocrInEntered;
}

/** Normalise a date-ish value (ISO string, timestamp, Date) to YYYY-MM-DD, or null. */
export function normalizeDob(value) {
    if (!value) {
        return null;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return value.slice(0, 10);
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    // Use UTC parts to avoid a timezone shifting the calendar day.
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Compare OCR'd DOB against the patient-entered DOB.
 * Returns null when either side is missing.
 * @returns {boolean|null}
 */
export function dobsMatch(ocrDob, enteredDob) {
    const ocr = normalizeDob(ocrDob);
    const entered = normalizeDob(enteredDob);
    if (!ocr || !entered) {
        return null;
    }
    return ocr === entered;
}
